import React from 'react'
import { router } from '@inertiajs/react'
import OperatorLayout from '@/layouts/OperatorLayout'
import { HelpTooltip } from '@/components/HelpTooltip'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ChevronLeft,
  ChevronRight,
  Filter,
  X,
  CalendarCheck,
  CheckCircle2,
  XCircle,
  Clock,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatMoney, formatCount } from '@/lib/format'
import type { SharedProps } from '@/types/shared'

interface ReservationEntry {
  id: number
  reservationRef: string
  roomType: string
  guestName: string | null
  checkIn: string | null
  checkOut: string | null
  bookingDate: string | null
  total: number | null
  adr: number | null
  nights: number
  status: string
  downloadedAt: string
}

interface Pagination {
  page: number
  pages: number
  count: number
}

interface Totals {
  reservations: number
  nights: number
  totalRevenue: number
  adr: number
}

interface Filters {
  roomTypeId: string | null
  status: string | null
  startDate: string | null
  endDate: string | null
  dateType: string
}

interface ReservationsProps extends SharedProps {
  entries: ReservationEntry[]
  pagination: Pagination
  totals: Totals
  roomTypes: { id: number; name: string }[]
  filters: Filters
}

const statusConfig: Record<string, { icon: React.ElementType; label: string; bg: string; color: string }> = {
  confirmed: { icon: CheckCircle2, label: 'Confirmed', bg: 'bg-emerald-50', color: 'text-emerald-700' },
  cancelled: { icon: XCircle, label: 'Cancelled', bg: 'bg-rose-50', color: 'text-rose-700' },
  pending: { icon: Clock, label: 'Pending', bg: 'bg-amber-50', color: 'text-amber-700' },
}

const dateTypeLabels: Record<string, string> = {
  booking: 'Booking date',
  check_in: 'Check-in date',
  stay: 'Stay date',
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

export default function Reservations({
  currentUser,
  property,
  properties,
  entries,
  pagination,
  totals,
  roomTypes,
  filters,
}: ReservationsProps) {
  const basePath = `${property.basePath}/reports/reservations`
  const cur = property.currency

  function applyFilters(updates: Partial<Filters> & { page?: number }) {
    const merged = { ...filters, ...updates }
    const params: Record<string, string> = {}
    if (merged.roomTypeId) params.room_type_id = merged.roomTypeId
    if (merged.status) params.status = merged.status
    if (merged.startDate) params.start_date = merged.startDate
    if (merged.endDate) params.end_date = merged.endDate
    if (merged.dateType && merged.dateType !== 'check_in') params.date_type = merged.dateType
    if (updates.page && updates.page > 1) params.page = String(updates.page)
    router.visit(basePath, { data: params, preserveState: true })
  }

  function clearFilters() {
    router.visit(basePath)
  }

  const hasActiveFilters =
    filters.roomTypeId || filters.status || filters.startDate || filters.endDate || filters.dateType !== 'check_in'

  return (
    <OperatorLayout title="Reservations" currentUser={currentUser} property={property} properties={properties}>
      <div className="space-y-6">
        <div className="animate-slide-in" style={{ animationDelay: '0ms' }}>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold tracking-tight text-foreground">
              Reservations Report
            </h1>
            <HelpTooltip content="A filterable list of every booking from your PMS. Use the date type to switch between booking-made, arrival, and stay-overlap views." />
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">
            All bookings for {property.name}, with totals across the filtered set.
          </p>
        </div>

        {/* Summary cards */}
        <div className="animate-slide-in grid gap-4 sm:grid-cols-2 lg:grid-cols-4" style={{ animationDelay: '50ms' }}>
          <SummaryTile label="Reservations" value={formatCount(totals.reservations)} />
          <SummaryTile label="Nights" value={formatCount(totals.nights)} />
          <SummaryTile label="Blended ADR" value={formatMoney(totals.adr, cur)} />
          <SummaryTile label="Total Revenue" value={formatMoney(totals.totalRevenue, cur)} highlight />
        </div>

        {/* Filter Bar */}
        <div className="animate-slide-in rounded-xl border border-border/60 bg-white p-4 shadow-sm" style={{ animationDelay: '100ms' }}>
          <div className="mb-3 flex items-center gap-2">
            <Filter className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Filters</span>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto h-6 px-2 text-xs text-muted-foreground"
                onClick={clearFilters}
              >
                <X className="mr-1 h-3 w-3" />
                Clear
              </Button>
            )}
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <FilterField label="Room Type">
              <Select
                value={filters.roomTypeId || 'all'}
                onValueChange={(v) => applyFilters({ roomTypeId: v === 'all' ? null : v })}
              >
                <SelectTrigger className="rounded-lg border-border/60">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Room Types</SelectItem>
                  {roomTypes.map((rt) => (
                    <SelectItem key={rt.id} value={String(rt.id)}>
                      {rt.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterField>

            <FilterField label="Status">
              <Select
                value={filters.status || 'all'}
                onValueChange={(v) => applyFilters({ status: v === 'all' ? null : v })}
              >
                <SelectTrigger className="rounded-lg border-border/60">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </FilterField>

            <FilterField label="Date Type">
              <Select
                value={filters.dateType}
                onValueChange={(v) => applyFilters({ dateType: v })}
              >
                <SelectTrigger className="rounded-lg border-border/60">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="check_in">Check-in date</SelectItem>
                  <SelectItem value="booking">Booking date</SelectItem>
                  <SelectItem value="stay">Stay date</SelectItem>
                </SelectContent>
              </Select>
            </FilterField>

            <FilterField label="Start Date">
              <Input
                type="date"
                value={filters.startDate || ''}
                onChange={(e) => applyFilters({ startDate: e.target.value || null })}
              />
            </FilterField>

            <FilterField label="End Date">
              <Input
                type="date"
                value={filters.endDate || ''}
                onChange={(e) => applyFilters({ endDate: e.target.value || null })}
              />
            </FilterField>
          </div>

          {(filters.startDate || filters.endDate) && (
            <p className="mt-3 text-[11px] text-muted-foreground">
              Date range filters by{' '}
              <span className="font-medium text-foreground">{dateTypeLabels[filters.dateType]}</span>.
            </p>
          )}
        </div>

        {/* Table */}
        <div className="animate-slide-in" style={{ animationDelay: '150ms' }}>
          {entries.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm">
              <CalendarCheck className="mx-auto h-8 w-8 text-muted-foreground/40" />
              <p className="mt-3 text-sm font-medium text-foreground">No reservations match these filters.</p>
              <p className="mt-1 text-xs text-muted-foreground">Try widening the date range or clearing the status filter.</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Reservation</TableHead>
                      <TableHead>Room Type</TableHead>
                      <TableHead>Check-in</TableHead>
                      <TableHead>Check-out</TableHead>
                      <TableHead>Booked</TableHead>
                      <TableHead className="text-right">Nights</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">ADR</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((entry) => {
                      const cfg = statusConfig[entry.status] || statusConfig.confirmed
                      const StatusIcon = cfg.icon

                      return (
                        <TableRow key={entry.id}>
                          <TableCell>
                            <div className="text-[13px] font-semibold text-foreground tabular-nums">
                              {entry.reservationRef}
                            </div>
                            {entry.guestName && (
                              <div className="text-[11px] text-muted-foreground">{entry.guestName}</div>
                            )}
                          </TableCell>
                          <TableCell className="text-[13px] text-foreground">{entry.roomType}</TableCell>
                          <TableCell className="text-[13px] text-muted-foreground whitespace-nowrap">{formatDate(entry.checkIn)}</TableCell>
                          <TableCell className="text-[13px] text-muted-foreground whitespace-nowrap">{formatDate(entry.checkOut)}</TableCell>
                          <TableCell className="text-[13px] text-muted-foreground whitespace-nowrap">{formatDate(entry.bookingDate)}</TableCell>
                          <TableCell className="text-right text-[13px] tabular-nums text-muted-foreground">{entry.nights}</TableCell>
                          <TableCell className="text-right text-[13px] font-semibold tabular-nums text-foreground">
                            {entry.total != null ? formatMoney(entry.total, cur) : '—'}
                          </TableCell>
                          <TableCell className="text-right text-[13px] tabular-nums text-muted-foreground">
                            {entry.adr != null ? formatMoney(entry.adr, cur) : '—'}
                          </TableCell>
                          <TableCell>
                            <Badge className={cn('text-[10px] font-semibold gap-1 hover:opacity-100', cfg.bg, cfg.color)}>
                              <StatusIcon className="h-3 w-3" />
                              {cfg.label}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>

              {pagination.pages > 1 && (
                <div className="flex items-center justify-between border-t border-border/40 px-5 py-3">
                  <p className="text-xs text-muted-foreground">
                    Page {pagination.page} of {pagination.pages} ({formatCount(pagination.count)} total)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-lg"
                      disabled={pagination.page <= 1}
                      onClick={() => applyFilters({ page: pagination.page - 1 })}
                    >
                      <ChevronLeft className="mr-1 h-3.5 w-3.5" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-lg"
                      disabled={pagination.page >= pagination.pages}
                      onClick={() => applyFilters({ page: pagination.page + 1 })}
                    >
                      Next
                      <ChevronRight className="ml-1 h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </OperatorLayout>
  )
}

function SummaryTile({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={cn(
      'rounded-xl border border-border/60 bg-white p-4 shadow-sm',
      highlight && 'ring-1 ring-primary/20'
    )}>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={cn(
        'mt-1.5 text-xl font-bold tracking-tight tabular-nums',
        highlight ? 'text-primary' : 'text-foreground'
      )}>
        {value}
      </p>
    </div>
  )
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-[150px]">
      <Label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  )
}
